import Cookies from "js-cookie";
import { listOrganizations } from "../service/CallCenterOrgAdmin";
import { getStoredPermissions } from "./permissions";
import { isDesktopApp } from "./isDesktop";

export const ORG_CONTEXT_CHANGED_EVENT = "cc:organization-context-changed";
export const SELECTED_ORG_ID_KEY = "selectedOrgId";
export const SELECTED_ORG_NAME_KEY = "selectedOrgName";
export const ORGANIZATION_HEADER = "X-Organization-Id";

const normalizeStoredId = (value) => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed == null || parsed === "") return null;
    return String(parsed);
  } catch {
    return raw.replace(/"/g, "");
  }
};

export const normalizePlatformUserType = (raw) =>
  String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const isPlatformSuperAdmin = () => {
  try {
    const userType = normalizePlatformUserType(Cookies.get("userType"));
    if (userType === "super_admin" || userType === "superadmin") {
      return true;
    }
    if (!isDesktopApp()) {
      return false;
    }
    const role = normalizePlatformUserType(Cookies.get("userRole"));
    if (role === "super_admin" || role === "superadmin") {
      return true;
    }
    const stored = getStoredPermissions();
    return Boolean(stored?.fullAccess);
  } catch {
    return false;
  }
};

export const getSelectedOrganizationId = () => {
  if (typeof localStorage === "undefined") return null;
  return normalizeStoredId(localStorage.getItem(SELECTED_ORG_ID_KEY));
};

export const getSelectedOrganizationName = () => {
  if (typeof localStorage === "undefined") return "";
  return localStorage.getItem(SELECTED_ORG_NAME_KEY) || "";
};

export const setSelectedOrganization = (organizationId, organizationName) => {
  if (typeof localStorage === "undefined") return;
  if (organizationId == null) {
    localStorage.removeItem(SELECTED_ORG_ID_KEY);
    localStorage.removeItem(SELECTED_ORG_NAME_KEY);
  } else {
    localStorage.setItem(SELECTED_ORG_ID_KEY, String(organizationId));
    localStorage.setItem(SELECTED_ORG_NAME_KEY, organizationName || "");
  }
  window.dispatchEvent(new CustomEvent(ORG_CONTEXT_CHANGED_EVENT, {
    detail: {
      organizationId: organizationId != null ? String(organizationId) : null,
      organizationName: organizationName || "",
    },
  }));
};

export const buildOrganizationContextHeaders = () => {
  if (!isPlatformSuperAdmin()) {
    return {};
  }
  const organizationId = getSelectedOrganizationId();
  if (!organizationId) {
    return {};
  }
  return { [ORGANIZATION_HEADER]: organizationId };
};

let ensureOrgContextPromise = null;

/**
 * Super-admins need a selected organization before tenant-scoped call-center APIs work.
 * Resolves the stored selection or defaults to EEMAD (or the first active org).
 */
export const ensureSuperAdminOrganizationContext = async () => {
  if (!isPlatformSuperAdmin()) {
    return getSelectedOrganizationId();
  }

  const existing = getSelectedOrganizationId();
  if (existing) {
    return existing;
  }

  if (!ensureOrgContextPromise) {
    ensureOrgContextPromise = (async () => {
      try {
        const data = await listOrganizations({
          page: 0,
          size: 200,
          sortBy: "NAME",
          sortDir: "ASC",
          includeInactive: false,
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        const defaultOrg = items.find((org) => org.code === "EEMAD") || items[0];
        if (!defaultOrg?.id) {
          return null;
        }
        setSelectedOrganization(defaultOrg.id, defaultOrg.name || defaultOrg.code || "");
        return String(defaultOrg.id);
      } finally {
        ensureOrgContextPromise = null;
      }
    })();
  }

  return ensureOrgContextPromise;
};

export const isAdminOrganizationListRequest = (config) => {
  const method = String(config?.method || "get").toLowerCase();
  const url = String(config?.url || "");
  return method === "get" && url.startsWith("admin/organizations");
};
